import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { BrainImage } from "@/types";

export interface CreateBrainImagePayload {
  name: string;
  description: string;
  imageRef: string;
  digest?: string;
}

export interface UpdateBrainImagePayload {
  name?: string;
  description?: string;
  imageRef?: string;
  digest?: string | null;
  active?: boolean;
}

function useBrainImagesClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidateBrainImages() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("brainImages") });
}

export function useCreateBrainImage(): UseMutationResult<
  BrainImage,
  Error,
  CreateBrainImagePayload
> {
  const getClient = useBrainImagesClient();
  const invalidate = useInvalidateBrainImages();
  return useMutation({
    mutationFn: (input) => getClient().post<BrainImage>("/brain-images", input),
    onSuccess: invalidate,
  });
}

export function useUpdateBrainImage(): UseMutationResult<
  BrainImage,
  Error,
  { id: string } & UpdateBrainImagePayload
> {
  const getClient = useBrainImagesClient();
  const invalidate = useInvalidateBrainImages();
  return useMutation({
    mutationFn: ({ id, ...patch }) =>
      getClient().patch<BrainImage>(`/brain-images/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteBrainImage(): UseMutationResult<void, Error, string> {
  const getClient = useBrainImagesClient();
  const invalidate = useInvalidateBrainImages();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/brain-images/${id}`),
    onSuccess: invalidate,
  });
}
